import {defineStore} from 'pinia';
import {syncRefs, useLocalStorage} from '@vueuse/core';
import {computed, watch} from 'vue';
import type {ValidLocale} from '../api/models/global/locale';

const currentLocaleStorageKey = 'currentLocale';

function getLocale(storageLocale: string | null): ValidLocale {
    if (storageLocale && isValidLocale(storageLocale)) {
        return storageLocale;
    }

    const preferredLocale = navigator.language.substr(0, 2).toLowerCase();

    if (isValidLocale(preferredLocale)) {
        return preferredLocale;
    }

    return 'en';
}

function isValidLocale(locale: string): locale is ValidLocale {
    return (getAvailableLanguages() as string[]).includes(locale);
}

/**
 * Gets the current valid locale from either local storage or the preferred user language.
 *
 * This method should only be used outside any Vue context.
 * Within a Vue context, `useLocaleStore` should be used.
 */
export function getCurrentLocale(): string {
    const storageLocale = localStorage.getItem(currentLocaleStorageKey);
    return getLocale(storageLocale);
}

export function getAvailableLanguages(): ValidLocale[] {
    return ['de', 'en', 'fr', 'it', 'es'];
}

export const useLocaleStore = defineStore('locale', () => {
    /**
     * The locale which is set within local storage.
     * Can potentially not be contained within `availableLanguages`.
     */
    const storageLocale = useLocalStorage<string | null>(currentLocaleStorageKey, null);

    /**
     * The current, valid locale.
     */
    const currentLocale = computed(() => getLocale(storageLocale.value));

    syncRefs(currentLocale, storageLocale);

    /**
     * Update document language esp. for screen readers
     */
    watch(
        currentLocale,
        newLocale => {
            document.documentElement.lang = newLocale;
        },
        {immediate: true}
    );

    /**
     * Sets the new target locale, if it is found
     * within the available languages.
     * @param locale Target locale.
     */
    function updateLocale(locale: string) {
        if (!isValidLocale(locale)) {
            return;
        }

        storageLocale.value = locale;
    }

    /**
     * Languages for which translations are available
     */
    const availableLanguages = getAvailableLanguages();

    const supportedLocale = computed(() =>
        ['en', 'de'].includes(currentLocale.value) ? currentLocale.value : 'en'
    );

    const dayjsLocale = computed(() => {
        if (currentLocale.value === 'en') {
            return 'en-gb';
        }

        return currentLocale.value;
    });

    return {
        currentLocale,
        supportedLocale,
        storageLocale,
        availableLanguages,
        updateLocale,
        dayjsLocale,
    };
});
